import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { Shield, AlertTriangle, Search, Filter, CheckCircle, XCircle, Clock, Eye, Edit } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const AdminSecurityReports = () => {
  const { locale } = useLanguage();
  const [sectionType, setSectionType] = useState('security'); // security, support
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    reportType: '',
    userId: ''
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [adminNotes, setAdminNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    setCurrentPage(0);
    loadReports();
  }, [filters, sectionType]);

  useEffect(() => {
    if (allReports.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setReports(allReports.slice(start, start + ITEMS_PER_PAGE));
    }
  }, [currentPage, allReports]);

  const loadReports = async () => {
    setLoading(true);
    try {
      if (sectionType === 'support') {
        const response = await adminApi.getSupportTickets(
          {
            status: filters.status,
            userId: filters.userId
          },
          1000,
          0
        );
        if (response?.success) {
          const allReportsData = (response.tickets || []).map((ticket) => ({
            id: ticket.id,
            title: ticket.subject,
            description: ticket.initialMessage,
            status: ticket.status,
            reportType: 'support',
            userId: ticket.userId,
            username: ticket.username,
            email: ticket.email,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt,
            botReply: ticket.botReply,
            isSupportTicket: true
          }));
          setAllReports(allReportsData);
          const start = currentPage * ITEMS_PER_PAGE;
          setReports(allReportsData.slice(start, start + ITEMS_PER_PAGE));
        } else {
          setReports([]);
          setAllReports([]);
        }
      } else {
        const response = await adminApi.getSecurityReports(filters, 1000, 0);
        if (response?.success) {
          const allReportsData = response.reports || [];
          setAllReports(allReportsData);
          const start = currentPage * ITEMS_PER_PAGE;
          setReports(allReportsData.slice(start, start + ITEMS_PER_PAGE));
        } else {
          setReports([]);
          setAllReports([]);
        }
      }
    } catch (error) {
      console.error('Error loading security reports:', error);
      setReports([]);
      setAllReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      if (sectionType === 'support') {
        const report = allReports.find((item) => item.id === reportId);
        if (report) {
          setSelectedReport(report);
          setAdminNotes('');
          setShowDetailModal(true);
        }
      } else {
        const response = await adminApi.getSecurityReport(reportId);
        if (response?.success) {
          setSelectedReport(response.report);
          setAdminNotes(response.report.adminNotes || '');
          setShowDetailModal(true);
        }
      }
    } catch (error) {
      console.error('Error loading report details:', error);
      alert('Failed to load report details');
    }
  };

  const handleUpdateStatus = async (reportId, newStatus) => {
    const message = sectionType === 'support'
      ? `Are you sure you want to mark this ticket as ${newStatus}?`
      : (newStatus === 'resolved' || newStatus === 'dismissed') 
        ? `Are you sure you want to mark this report as ${newStatus}? The report will be permanently deleted from the database.`
        : `Are you sure you want to mark this report as ${newStatus}?`;
    
    if (!window.confirm(message)) {
      return;
    }

    setUpdatingStatus(true);
    try {
      if (sectionType === 'support') {
        const response = await adminApi.updateSupportTicketStatus(reportId, newStatus);
        if (response?.success) {
          setShowDetailModal(false);
          setSelectedReport(null);
          setAdminNotes('');
          alert('Ticket status updated successfully');
          await loadReports();
        }
      } else {
        const response = await adminApi.updateSecurityReportStatus(reportId, newStatus, adminNotes);
        if (response?.success) {
          // If report was deleted (resolved/dismissed), close modal and reload list
          if (response.deleted || newStatus === 'resolved' || newStatus === 'dismissed') {
            setShowDetailModal(false);
            setSelectedReport(null);
            setAdminNotes('');
            alert('Report resolved and deleted successfully');
          } else {
            // For other statuses, update the selected report
            if (selectedReport?.id === reportId) {
              setSelectedReport(response.report);
            }
            alert('Report status updated successfully');
          }
          await loadReports();
        }
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      alert('Failed to update report status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(locale || 'it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'investigating': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'dismissed': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'closed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const totalPages = Math.ceil(allReports.length / ITEMS_PER_PAGE);

  return (
    <div className="admin-container p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
          <Shield className="w-8 h-8 mr-3" />
          {sectionType === 'support' ? 'Support Tickets' : 'Security Reports'}
        </h1>
        <p className="text-gray-400">
          {sectionType === 'support'
            ? 'Gestisci richieste di supporto utenti'
            : 'Manage and respond to user security reports'}
        </p>
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <button
          onClick={() => setSectionType('security')}
          className={`px-4 py-2 rounded-lg font-semibold ${
            sectionType === 'security'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Security Reports
        </button>
        <button
          onClick={() => setSectionType('support')}
          className={`px-4 py-2 rounded-lg font-semibold ${
            sectionType === 'support'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Support Tickets
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 border border-gray-600"
            >
              <option value="">All Status</option>
              {sectionType === 'support' ? (
                <>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="closed">Closed</option>
                </>
              ) : (
                <>
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </>
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 border border-gray-600"
              disabled={sectionType === 'support'}
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Report Type</label>
            <select
              value={filters.reportType}
              onChange={(e) => setFilters({ ...filters, reportType: e.target.value })}
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 border border-gray-600"
              disabled={sectionType === 'support'}
            >
              <option value="">All Types</option>
              <option value="account_compromise">Account Compromise</option>
              <option value="suspicious_activity">Suspicious Activity</option>
              <option value="phishing">Phishing</option>
              <option value="unauthorized_access">Unauthorized Access</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">User ID</label>
            <input
              type="number"
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              placeholder="Filter by User ID"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 border border-gray-600"
            />
          </div>
        </div>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">
            {sectionType === 'support' ? 'Loading support tickets...' : 'Loading security reports...'}
          </p>
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-gray-800/50 rounded-lg p-12 text-center border border-gray-700">
          <Shield className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">
            {sectionType === 'support' ? 'No support tickets found' : 'No security reports found'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-yellow-500/50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{report.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(report.status)}`}>
                        {report.status}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold border ${getPriorityColor(report.priority)}`}>
                        {report.priority}
                      </span>
                    </div>
                    <p className="text-gray-300 text-sm mb-2 line-clamp-2">{report.description}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-400">
                      <span>Type: {report.reportType}</span>
                      <span>User: {report.username || `ID: ${report.userId}`}</span>
                      <span>Created: {formatDate(report.createdAt)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewReport(report.id)}
                    className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  currentPage === 0
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Previous
              </button>
              <span className="text-gray-400">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  currentPage >= totalPages - 1
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Report Detail Modal */}
      {showDetailModal && selectedReport && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">
                {selectedReport?.isSupportTicket ? 'Ticket Details' : 'Report Details'}
              </h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedReport(null);
                  setAdminNotes('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <p className="text-white font-semibold">{selectedReport.title}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {selectedReport?.isSupportTicket ? 'User Message' : 'Description'}
                </label>
                <p className="text-white">{selectedReport.description}</p>
              </div>

              {selectedReport?.isSupportTicket && selectedReport.botReply && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Bot Reply</label>
                  <p className="text-white">{selectedReport.botReply}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                  <span className={`inline-block px-3 py-1 rounded text-sm font-semibold border ${getStatusColor(selectedReport.status)}`}>
                    {selectedReport.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Priority</label>
                  <span className={`inline-block px-3 py-1 rounded text-sm font-semibold border ${getPriorityColor(selectedReport.priority)}`}>
                    {selectedReport.priority}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {selectedReport?.isSupportTicket ? 'Category' : 'Report Type'}
                  </label>
                  <p className="text-white">{selectedReport.reportType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">User</label>
                  <p className="text-white">{selectedReport.username || `ID: ${selectedReport.userId}`}</p>
                  {selectedReport.email && (
                    <p className="text-gray-400 text-sm">{selectedReport.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Created</label>
                  <p className="text-white text-sm">{formatDate(selectedReport.createdAt)}</p>
                </div>
                {selectedReport.resolvedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Resolved</label>
                    <p className="text-white text-sm">{formatDate(selectedReport.resolvedAt)}</p>
                    {selectedReport.resolvedByUsername && (
                      <p className="text-gray-400 text-xs">By: {selectedReport.resolvedByUsername}</p>
                    )}
                  </div>
                )}
              </div>

              {!selectedReport?.isSupportTicket && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Admin Notes</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 border border-gray-600"
                      rows={4}
                      placeholder="Add internal notes about this report..."
                    />
                  </div>

                  {selectedReport.adminNotes && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <label className="block text-sm font-medium text-blue-300 mb-1">Current Notes</label>
                      <p className="text-blue-200 text-sm">{selectedReport.adminNotes}</p>
                    </div>
                  )}
                </>
              )}

              <div className="flex space-x-3 pt-4">
                {selectedReport?.isSupportTicket ? (
                  <>
                    {selectedReport.status !== 'closed' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'closed')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Close
                      </button>
                    )}
                    {selectedReport.status !== 'in_progress' && selectedReport.status !== 'closed' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'in_progress')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        In Progress
                      </button>
                    )}
                    {selectedReport.status === 'closed' ? (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'open')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Reopen
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>
                    {selectedReport.status !== 'resolved' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'resolved')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Resolved
                      </button>
                    )}
                    {selectedReport.status !== 'investigating' && selectedReport.status !== 'resolved' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'investigating')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Mark as Investigating
                      </button>
                    )}
                    {selectedReport.status !== 'dismissed' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'dismissed')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Dismiss
                      </button>
                    )}
                    {selectedReport.status === 'resolved' || selectedReport.status === 'dismissed' ? (
                      <button
                        onClick={() => handleUpdateStatus(selectedReport.id, 'open')}
                        disabled={updatingStatus}
                        className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Reopen
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSecurityReports;
